'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import slugify from 'slugify'
import { deleteCookie } from 'cookies-next/client'
import { redirect, usePathname } from 'next/navigation'
import Link from 'next/link'
import { ArrowRightEndOnRectangleIcon, PlusIcon, MagnifyingGlassIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'

import { lookup } from '@cruz-com/lookup'
import { pages, popularCategories, infoPages, researchPages } from '@/settings'
import { useClose } from '@headlessui/react'
import { useGlobalStore } from '@/store/provider'
import { Logo } from '@/components'

const menuStyle = 'px-4 py-3 text-base bg-white dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 flex items-center gap-x-2 truncate'
const categoryStyle = 'text-pretty text-xl font-bold px-4 py-2 bg-white dark:bg-zinc-800'
const gridStyle = 'grid grid-cols-2 gap-[1px] bg-zinc-200 dark:bg-zinc-700 border-t border-b mb-4'

type MenuProps = {
  name: string
  menu?: string
  icon?: React.ReactNode
  href: string
  count?: number
}

export function NavSide() {
  const pathname = usePathname()
  const { user, setUser, setToast, setShowFeedback } = useGlobalStore((state) => state)
  const close = useClose()

  function signOut() {
    close()
    setUser(null, null)
    deleteCookie('access_token')
    deleteCookie('refresh_token')
    if (_.includes(pathname, '/my')) {
      redirect(pages.home.href)
    } else {
      setToast({ type: 'success', message: 'Successfully signed out' })
    }
  }

  function showFeedback() {
    close()
    setShowFeedback(true)
  }

  const Menu = ({ icon, name, menu, href, count }: MenuProps) =>
    <Link hrefLang='en' href={href} className={menuStyle} onClick={close}>
      <span className='flex items-center gap-1.5'>
        {icon}
        {menu || name}
      </span>
      {!_.isUndefined(count) && <span className='ml-1 border rounded-full px-1.5 py-0.5 text-xs font-normal'>{count}</span>}
    </Link>


  return (
    <div className='flex grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-zinc-800 pt-8 pb-16'>
      <div className='flex justify-start px-4 pb-2'>
        <Logo size='w-28' />
      </div>
      <nav className='flex flex-1 flex-col'>
        <div className='divide-y border-t border-b'>
          <div className='grid grid-cols-2 divide-x'>
            <Menu {...pages.mySavedSearch} count={user?.statistic?.saved_search} />
            <Menu {...pages.mySavedVehicle} count={user?.statistic?.saved_vehicle} />
          </div>
          <Link hrefLang='en' href={pages.vehicleSearch.href} className={twMerge(menuStyle, 'text-lg font-medium')} onClick={close}>
            <MagnifyingGlassIcon className='size-6' />
            Search All Vehicles
          </Link>
        </div>
        <div className={twMerge(categoryStyle, 'mt-4')}>
          Popular Categories
        </div>
        <ul role='list' className={gridStyle}>
          {_.map(popularCategories, (d, idx) => <Menu key={`pc_${idx}`} {...d} />)}
          <li className='bg-white dark:bg-zinc-800'></li>
        </ul>
        <div className={categoryStyle}>Body Styles</div>
        <div className={gridStyle}>
          {
            _.map(lookup.vehicle.bodyType, (v, idx) =>
              <Link hrefLang='en' key={idx} href={`/vehicles/body-style-s/${slugify(v.sName)}/${v.id}`} className={menuStyle} onClick={close}>
                {v.sName}
              </Link>
            )
          }
        </div>
        <div className={twMerge(categoryStyle, 'mt-4')}>Research</div>
        <div className='divide-y border-t border-b'>
          {_.map(researchPages, (d, idx) => <Menu key={`ips_${idx}`} {...d} />)}
        </div>
        <div className={twMerge(categoryStyle, 'mt-4')}>Spotted</div>
        <div className='divide-y border-t border-b'>
          <Menu {...pages.spotted} />
          <Menu {...pages.postSpotted} />
        </div>
        <div className={twMerge(categoryStyle, 'mt-4')}>For Dealers</div>
        <div className='divide-y border-t border-b'>
          <Menu {...pages.forDealers} />
        </div>
        <div className={twMerge(categoryStyle, 'mt-4')}>My Cruz</div>
        {
          user
            ?
            <div className='divide-y border-t border-b'>
              <Menu {...pages.myCruz} />
              <Menu {...pages.mySavedSearch} count={user?.statistic?.saved_search || 0} />
              <Menu {...pages.mySavedVehicle} count={user?.statistic?.saved_vehicle || 0} />
              <Menu {...pages.myInquiries} count={user?.statistic?.inquiry_count || 0} />
              <Menu {...pages.myVehicles} count={user?.statistic?.vehicle?.all || 0} />
              <Menu {...pages.postVehicle} icon={<PlusIcon className='w-4' />} />
              <Menu {...pages.mySpotted} count={user?.statistic?.spotted_count || 0} />
              <Menu {...pages.postSpotted} icon={<PlusIcon className='w-4' />} />
              <Menu {...pages.myProfile} />
              <Menu {...pages.myPassword} />
              <button type='button' className={menuStyle} onClick={signOut}>
                <ArrowRightEndOnRectangleIcon className='w-4' />
                Sign Out
              </button>
            </div>
            :
            <div className='divide-y border-t border-b'>
              <Menu {...pages.signIn} />
              <Menu {...pages.forgotPassword} />
              <Menu {...pages.joinCruz} />
            </div>
        }
        <div className={twMerge(categoryStyle, 'mt-4')}>About</div>
        <div className='divide-y border-t border-b'>
          {_.map(infoPages, (d, idx) => <Menu key={`ips_${idx}`} {...d} />)}
          <button type='button' className={twMerge(menuStyle, 'w-full')} onClick={showFeedback}>
            <ChatBubbleLeftRightIcon className='size-5 mr-2' />
            Feedback
          </button>
        </div>
      </nav>
    </div >
  )
}